import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { Input } from '../../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { facturamaService, CFDI_CANCEL_MOTIVES, CfdiCancelMotive } from '../../../services/facturama.service'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useSessionStore } from '../../../store/auth/auth.store'

interface CancelCfdiModalProps {
  isOpen: boolean
  onClose: () => void
  /** ID del CFDI en Facturama */
  cfdiId: string
  /** Folio legible para mostrar al usuario (ej. "A-001") */
  cfdiLabel?: string
  /** ID de la venta local (para actualizar cfdi_cancelled_at en BD) */
  saleId?: number | string
  /** Callback tras cancelación exitosa */
  onSuccess?: () => void
}

/**
 * Modal de confirmación para cancelar un CFDI ante el SAT.
 * Permite elegir el motivo (01-04) y el UUID de sustitución (solo motivo 01).
 * No cancela la venta.
 */
export function CancelCfdiModal({
  isOpen,
  onClose,
  cfdiId,
  cfdiLabel,
  saleId,
  onSuccess,
}: CancelCfdiModalProps) {
  const canCancelSale = useSessionStore((state) => state.canAction('sales.sale.cancel'))
  const queryClient = useQueryClient()
  const [motive, setMotive] = useState<CfdiCancelMotive>('02')
  const [uuidReplacement, setUuidReplacement] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const requiresReplacement = motive === '01'

  const handleSubmit = async () => {
    if (!canCancelSale) return

    if (requiresReplacement && !uuidReplacement.trim()) {
      toast.error('El motivo 01 requiere el UUID del CFDI sustituto.')
      return
    }

    setIsSubmitting(true)
    try {
      await facturamaService.cancelInvoice(
        cfdiId,
        motive,
        requiresReplacement ? uuidReplacement.trim() : undefined,
        saleId,
      )
      toast.success('CFDI cancelado correctamente ante el SAT.')
      queryClient.invalidateQueries({ queryKey: ['facturama-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      if (saleId) {
        queryClient.invalidateQueries({ queryKey: ['saleDetail', saleId] })
      }
      onSuccess?.()
      onClose()
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al cancelar el CFDI.'
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setMotive('02')
      setUuidReplacement('')
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Cancelar CFDI ante el SAT
          </DialogTitle>
          <DialogDescription>
            {cfdiLabel
              ? `Vas a cancelar la factura ${cfdiLabel} en el SAT a través de Facturama.`
              : 'Vas a cancelar esta factura en el SAT a través de Facturama.'}
          </DialogDescription>
        </DialogHeader>

        {/* Alerta */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 leading-relaxed">
          <strong>Importante:</strong> Esta acción cancela únicamente la factura CFDI. La venta
          permanece activa y no se revierte el inventario ni el cobro.
        </div>

        <div className="space-y-4 py-2">
          {/* Motivo */}
          <div className="space-y-2">
            <Label htmlFor="cfdi-motive">
              Motivo de cancelación <span className="text-red-500">*</span>
            </Label>
            <Select
              value={motive}
              onValueChange={(v) => {
                setMotive(v as CfdiCancelMotive)
                if (v !== '01') setUuidReplacement('')
              }}
            >
              <SelectTrigger id="cfdi-motive" className="w-full">
                <SelectValue placeholder="Selecciona el motivo" />
              </SelectTrigger>
              <SelectContent>
                {CFDI_CANCEL_MOTIVES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* UUID de sustitución — solo motivo 01 */}
          {requiresReplacement && (
            <div className="space-y-2">
              <Label htmlFor="cfdi-uuid-replacement">
                UUID del CFDI sustituto <span className="text-red-500">*</span>
              </Label>
              <Input
                id="cfdi-uuid-replacement"
                value={uuidReplacement}
                onChange={(e) => setUuidReplacement(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                disabled={isSubmitting}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                El UUID del CFDI que sustituye a este comprobante.
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            variant="destructive"
            className="flex-1"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cancelando...</>
            ) : (
              'Confirmar Cancelación'
            )}
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
